import type { Data } from "@measured/puck";

/**
 * i18n de contenu : déshydratation (BO, par type de bloc) / réhydratation
 * (générique). Source de vérité des champs traduisibles = TRANSLATABLE_FIELDS.
 */

/**
 * Champs traduisibles par type de bloc (R1) — clé ContentString `<blockId>.<field>`.
 * Le BO est seul à connaître ces champs ; l'API reste agnostique des blocs.
 *
 * Lot 1 média : `coverAttachmentId` (Hero) / `attachmentId` (Media) sont
 * STRUCTURELS — ils NE sont PAS listés ici, donc restent dans le layout
 * (partagés toutes langues), jamais extraits vers ContentString. Légende/alt
 * d'un média viennent des champs multilingues de l'Attachment ; `caption` du
 * bloc Media reste une légende libre traduisible.
 */
const TRANSLATABLE_FIELDS: Record<string, string[]> = {
  Cta: ["title", "text", "buttonLabel"],
  Banner: ["text", "linkLabel"],
  // TextImage : contenu traduisible via le slot `content` (extraction récursive) ;
  // `imageCaption` (légende/crédits sous l'image) est un champ PLAT traduisible.
  TextImage: ["imageCaption"],
  // Accordéon : seuls les TITRES sont plats/traduisibles ; les réponses sont des
  // slots (primitives) → extraites récursivement (cf. walkPuckNodes).
  Accordion: ["item1Title", "item2Title", "item3Title", "item4Title"],
  // Onglets : mêmes règles que l'Accordéon — seuls les TITRES sont plats/traduisibles ;
  // les corps sont des slots (primitives) extraits récursivement (cf. walkPuckNodes).
  Tabs: ["tab1Title", "tab2Title", "tab3Title", "tab4Title"],
  Media: ["caption"],
  RichText: ["body"],
  // Lot 4 — blocs de contenu simples (champs PLATS uniquement). Imbriqués dans
  // un conteneur, leur extraction est récursive (cf. walkPuckNodes). Les champs
  // structurels (level/align/url/variant/name/size/color/style…) ne sont PAS
  // listés → restent dans le layout, partagés toutes langues.
  Heading: ["text"],
  Button: ["label"],
  // Téléchargement : l'URL est traduisible EXPRÈS (un document PAR LANGUE,
  // fallback langue défaut) — parité documentUrl multilingue de l'Expertise.
  Download: ["label", "url"],
  Quote: ["text", "author"],
  // `html` est du HTML brut traduisible → assaini au save côté API (clé `.html`).
  Html: ["html"],
  PaymentButton: ["label"],
  ExpertiseOffers: ["title"],
  CatalogueCtaButton: ["label"],
  PdfReader: ["title"],
};

/**
 * Blocs dont les ITEMS (`props.items: SelectionItem[]`) portent une légende
 * custom TRADUISIBLE (`item.caption`, parité ancien CMS `Selection.caption`,
 * affichée via la bascule `customField`). Clé ContentString dédiée :
 * `<blockId>.captions.<type>:<id>` (l'itemKey ne contient jamais de point).
 * Le layout NEUTRE stocke les items SANS caption (structure partagée).
 *
 * `SelectionSections` : légendes globales (`captions.<type>:<id>`) + par
 * catégorie (`captions.<catId>.<type>:<id>`) + titre/desc
 * (`categories.<catId>.title|description`).
 */
const SELECTION_CAPTION_BLOCKS = new Set<string>([
  "ArtworkSelection",
  "SelectionSections",
]);

/** Préfixe des clés de légendes par item dans `strings`. */
const CAPTIONS_PREFIX = "captions.";
/** Préfixe des champs traduisibles des rubriques SelectionSections. */
const CATEGORIES_PREFIX = "categories.";

type CaptionedItem = { type?: string; id?: string; caption?: unknown };
type CategoryNode = {
  id?: string;
  title?: unknown;
  description?: unknown;
  items?: CaptionedItem[];
};

/** Clé d'item pour les légendes (mirroir de `catalogItemKey`). */
function captionItemKey(it: CaptionedItem): string {
  return `${it?.type ?? ""}:${it?.id ?? ""}`;
}

function stripItemCaptions(items: CaptionedItem[]): CaptionedItem[] {
  return items.map((it) => {
    if (!it || typeof it !== "object") return it;
    const { caption: _caption, ...rest } = it;
    return rest;
  });
}

/** Extrait titres/descriptions/légendes d'un bloc SelectionSections. */
function extractSelectionSectionsStrings(
  blockId: string,
  props: Record<string, unknown>,
  strings: Record<string, string>,
): void {
  const categories = props.categories;
  if (Array.isArray(categories)) {
    for (const cat of categories as CategoryNode[]) {
      if (!cat || typeof cat !== "object" || !cat.id) continue;
      const catId = String(cat.id);
      strings[`${blockId}.${CATEGORIES_PREFIX}${catId}.title`] =
        typeof cat.title === "string" ? cat.title : "";
      strings[`${blockId}.${CATEGORIES_PREFIX}${catId}.description`] =
        typeof cat.description === "string" ? cat.description : "";
      if (Array.isArray(cat.items)) {
        for (const it of cat.items) {
          if (!it || typeof it !== "object" || !it.id) continue;
          strings[
            `${blockId}.${CAPTIONS_PREFIX}${catId}.${captionItemKey(it)}`
          ] = typeof it.caption === "string" ? it.caption : "";
        }
      }
    }
  }
  const items = props.items;
  if (Array.isArray(items)) {
    for (const it of items as CaptionedItem[]) {
      if (!it || typeof it !== "object" || !it.id) continue;
      strings[`${blockId}.${CAPTIONS_PREFIX}${captionItemKey(it)}`] =
        typeof it.caption === "string" ? it.caption : "";
    }
  }
}

/** Neutralise titres/descriptions/légendes d'un SelectionSections. */
function neutralizeSelectionSections(
  props: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...props };
  if (Array.isArray(props.categories)) {
    next.categories = (props.categories as CategoryNode[]).map((cat) => {
      if (!cat || typeof cat !== "object") return cat;
      return {
        ...cat,
        title: "",
        description: "",
        items: Array.isArray(cat.items)
          ? stripItemCaptions(cat.items)
          : cat.items,
      };
    });
  }
  if (Array.isArray(props.items)) {
    next.items = stripItemCaptions(props.items as CaptionedItem[]);
  }
  return next;
}

/**
 * Réinjecte catégories + légendes depuis les overrides ContentString.
 * KEEP IN SYNC avec `hydratePuckLayout` (frontpublic).
 */
function applySelectionOverrides(
  props: Record<string, unknown>,
  overrides: Record<string, string>,
): Record<string, unknown> {
  const flat: Record<string, string> = {};
  const captions: Record<string, string> = {};
  const catFields = new Map<string, { title?: string; description?: string }>();
  const catCaptions = new Map<string, Record<string, string>>();

  for (const [field, value] of Object.entries(overrides)) {
    if (field.startsWith(CAPTIONS_PREFIX)) {
      const rest = field.slice(CAPTIONS_PREFIX.length);
      // `captions.<catId>.<type>:<id>` vs `captions.<type>:<id>`
      const dot = rest.indexOf(".");
      if (dot > 0 && rest.includes(":")) {
        const maybeCatId = rest.slice(0, dot);
        const itemKey = rest.slice(dot + 1);
        if (itemKey.includes(":")) {
          const map = catCaptions.get(maybeCatId) ?? {};
          map[itemKey] = value;
          catCaptions.set(maybeCatId, map);
          continue;
        }
      }
      captions[rest] = value;
    } else if (field.startsWith(CATEGORIES_PREFIX)) {
      // `categories.<catId>.title|description`
      const rest = field.slice(CATEGORIES_PREFIX.length);
      const dot = rest.lastIndexOf(".");
      if (dot <= 0) continue;
      const catId = rest.slice(0, dot);
      const key = rest.slice(dot + 1);
      if (key !== "title" && key !== "description") continue;
      const entry = catFields.get(catId) ?? {};
      entry[key] = value;
      catFields.set(catId, entry);
    } else {
      flat[field] = value;
    }
  }

  const next: Record<string, unknown> = { ...props, ...flat };

  if (
    (catFields.size > 0 || catCaptions.size > 0) &&
    Array.isArray(props.categories)
  ) {
    next.categories = (props.categories as CategoryNode[]).map((cat) => {
      if (!cat || typeof cat !== "object" || !cat.id) return cat;
      const catId = String(cat.id);
      const fields = catFields.get(catId);
      const caps = catCaptions.get(catId);
      let items = cat.items;
      if (caps && Array.isArray(items)) {
        items = items.map((it) => {
          const caption = caps[captionItemKey(it)];
          return caption != null ? { ...it, caption } : it;
        });
      }
      return {
        ...cat,
        ...(fields?.title != null ? { title: fields.title } : {}),
        ...(fields?.description != null
          ? { description: fields.description }
          : {}),
        ...(items ? { items } : {}),
      };
    });
  }

  if (Object.keys(captions).length > 0 && Array.isArray(props.items)) {
    next.items = (props.items as CaptionedItem[]).map((it) => {
      const caption = captions[captionItemKey(it)];
      return caption != null ? { ...it, caption } : it;
    });
  }

  return next;
}

/* -------------------------------------------------------------------------- */
/* Traversée récursive de l'arbre Puck (slots)                                 */
/* -------------------------------------------------------------------------- */

/**
 * Forme minimale d'un bloc CMS avancé (`content[i]`). Avec les SLOTS (Puck 0.20), un
 * bloc conteneur stocke ses enfants INLINE dans ses props : `props[slotName]`
 * est lui-même un tableau de blocs (`{ type, props }[]`). Il n'y a PAS de
 * `data.zones` pour les slots ; tout vit dans `content[].props`.
 */
type PuckNode = { type?: string; props?: Record<string, unknown> };

/** Un slot Puck = tableau de blocs imbriqués (`{ type, props }[]`). */
function isSlotArray(value: unknown): value is PuckNode[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v) =>
        v != null &&
        typeof v === "object" &&
        typeof (v as PuckNode).type === "string" &&
        typeof (v as PuckNode).props === "object",
    )
  );
}

/**
 * Helper de traversée PARTAGÉ : visite récursivement chaque bloc de l'arbre Puck
 * (top-level `content` + tout slot imbriqué dans `props`), parent d'abord.
 * `visit` peut muter `node.props` en place (les helpers ci-dessous clonent en
 * amont). On descend dans toute prop qui est un slot (tableau de blocs).
 */
function walkPuckNodes(
  nodes: PuckNode[] | undefined,
  visit: (node: PuckNode) => void,
): void {
  if (!Array.isArray(nodes)) return;
  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    visit(node);
    const props = node.props;
    if (props && typeof props === "object") {
      for (const value of Object.values(props)) {
        if (isSlotArray(value)) walkPuckNodes(value, visit);
      }
    }
  }
}

/**
 * Clone profond d'un tableau de blocs CMS avancé en re-visitant chaque bloc (parent
 * puis enfants des slots). `transform` reçoit un clone superficiel des props et
 * renvoie les props à utiliser ; les slots imbriqués sont clonés récursivement.
 */
function mapPuckNodes(
  nodes: PuckNode[],
  transform: (type: string, props: Record<string, unknown>) => Record<string, unknown>,
): PuckNode[] {
  return nodes.map((node) => {
    const props = (node.props ?? {}) as Record<string, unknown>;
    // Clone les slots imbriqués d'abord (récursion), puis applique transform.
    const nextProps: Record<string, unknown> = { ...props };
    for (const [key, value] of Object.entries(props)) {
      if (isSlotArray(value)) nextProps[key] = mapPuckNodes(value, transform);
    }
    const finalProps = transform(node.type ?? "", nextProps);
    return { ...node, props: finalProps };
  });
}

/**
 * Extrait les chaînes traduisibles d'un doc Puck → `{ "<blockId>.<field>": value }`.
 * Clé de bloc = `props.id` (id unique attribué par Puck sur TOUT l'arbre, slots
 * inclus), fallback sur le type. RÉCURSIF : descend dans les blocs imbriqués des
 * slots (conteneurs de mise en page) via `walkPuckNodes`.
 */
export function extractTranslatableStrings(
  data: Data,
): Record<string, string> {
  const strings: Record<string, string> = {};
  walkPuckNodes(data.content as PuckNode[], (node) => {
    const props = (node.props ?? {}) as Record<string, unknown> & { id?: string };
    const blockId = props.id ?? node.type ?? "";
    const fields = node.type ? TRANSLATABLE_FIELDS[node.type] : undefined;
    if (fields) {
      for (const field of fields) {
        const value = props[field];
        if (typeof value === "string" && value.length > 0) {
          strings[`${blockId}.${field}`] = value;
        }
      }
    }
    // Légendes par item (Sélection d'œuvres) → clés `<blockId>.captions.<itemKey>`.
    // On émet AUSSI la chaîne vide pour les items sans légende : c'est ce qui
    // permet au diff du save de détecter un EFFACEMENT (baseline non vide → "")
    // et de l'écrire — sinon la vieille légende ressusciterait au rechargement.
    // (diffStrings ne retient que les clés qui changent → pas de pollution.)
    if (node.type === "SelectionSections") {
      extractSelectionSectionsStrings(blockId, props, strings);
    } else if (node.type && SELECTION_CAPTION_BLOCKS.has(node.type)) {
      const items = props.items;
      if (Array.isArray(items)) {
        for (const it of items as CaptionedItem[]) {
          if (!it || typeof it !== "object" || !it.id) continue;
          strings[`${blockId}.${CAPTIONS_PREFIX}${captionItemKey(it)}`] =
            typeof it.caption === "string" ? it.caption : "";
        }
      }
    }
  });
  return strings;
}

/**
 * Déshydrate un doc Puck d'édition (texte en clair pour la langue courante) en :
 *  - `neutralLayout` : clone profond du doc avec chaque champ traduisible vidé ("")
 *    — la STRUCTURE (variant, align, artworkIds, ids de blocs, SLOTS imbriqués…)
 *    est conservée.
 *  - `strings` : `{ "<blockId>.<field>": value }` du texte extrait (arbre complet).
 * Le texte ne vit donc QUE dans `strings` (→ ContentString), jamais dans le layout.
 * RÉCURSIF : les blocs imbriqués dans les slots sont neutralisés comme le top-level.
 */
export function dehydrate(data: Data): {
  neutralLayout: Data;
  strings: Record<string, string>;
} {
  const strings = extractTranslatableStrings(data);
  const neutralLayout = {
    ...data,
    content: mapPuckNodes(data.content as PuckNode[], (type, props) => {
      if (type === "SelectionSections") {
        return neutralizeSelectionSections(props);
      }
      const fields = type ? TRANSLATABLE_FIELDS[type] : undefined;
      const hasCaptions =
        type && SELECTION_CAPTION_BLOCKS.has(type) && Array.isArray(props.items);
      if (!fields && !hasCaptions) return props;
      const nextProps: Record<string, unknown> = { ...props };
      for (const field of fields ?? []) {
        if (typeof props[field] === "string") nextProps[field] = "";
      }
      // Items SANS caption dans le layout neutre (le texte ne vit que dans strings).
      if (hasCaptions) {
        nextProps.items = stripItemCaptions(props.items as CaptionedItem[]);
      }
      return nextProps;
    }),
  } as Data;
  return { neutralLayout, strings };
}

/**
 * Réhydrate un layout NEUTRE avec un dictionnaire `strings`
 * (`{ "<blockId>.<field>": value }`) — GÉNÉRIQUE, sans connaissance des types de
 * blocs : pour chaque entrée, on retrouve le bloc dont `props.id === blockId`
 * (n'importe où dans l'arbre, slots inclus) et on pose `props[field] = value`.
 * RÉCURSIF. Retourne un nouveau doc (clone non destructif).
 */
export function hydrate(layout: Data, strings: Record<string, string>): Data {
  if (!strings || Object.keys(strings).length === 0) return layout;
  // Index des surcharges par blockId → { field: value }.
  const byBlock = new Map<string, Record<string, string>>();
  for (const [key, value] of Object.entries(strings)) {
    const dot = key.indexOf(".");
    if (dot <= 0) continue;
    const blockId = key.slice(0, dot);
    const field = key.slice(dot + 1);
    const entry = byBlock.get(blockId) ?? {};
    entry[field] = value;
    byBlock.set(blockId, entry);
  }
  return {
    ...layout,
    content: mapPuckNodes(layout.content as PuckNode[], (_type, props) => {
      const blockId = (props.id as string | undefined) ?? _type;
      const overrides = byBlock.get(blockId);
      if (!overrides) return props;
      // SelectionSections (catégories + légendes imbriquées) ou ArtworkSelection.
      if (
        _type === "SelectionSections" ||
        Array.isArray(props.categories) ||
        Object.keys(overrides).some(
          (k) =>
            k.startsWith(CATEGORIES_PREFIX) ||
            (k.startsWith(CAPTIONS_PREFIX) &&
              k.slice(CAPTIONS_PREFIX.length).includes(".")),
        )
      ) {
        return applySelectionOverrides(props, overrides);
      }
      // Sépare champs PLATS et légendes par item (`captions.<itemKey>`) : ces
      // dernières se réinjectent DANS `props.items[]` (item.caption), jamais en
      // prop plate.
      const flat: Record<string, string> = {};
      const captions: Record<string, string> = {};
      for (const [field, value] of Object.entries(overrides)) {
        if (field.startsWith(CAPTIONS_PREFIX)) {
          captions[field.slice(CAPTIONS_PREFIX.length)] = value;
        } else {
          flat[field] = value;
        }
      }
      const next: Record<string, unknown> = { ...props, ...flat };
      if (Object.keys(captions).length > 0 && Array.isArray(props.items)) {
        next.items = (props.items as CaptionedItem[]).map((it) => {
          const caption = captions[captionItemKey(it)];
          return caption != null ? { ...it, caption } : it;
        });
      }
      return next;
    }),
  } as Data;
}

/* -------------------------------------------------------------------------- */
/* Images par langue : base (layout) + surcharges (ContentString)             */
/* -------------------------------------------------------------------------- */

/**
 * Champs média MONO-IMAGE traduisibles par langue (Lot 1). Cf.
 * PER_LANGUAGE_IMAGES.md. Contrairement au texte, l'image a une valeur de BASE
 * partagée qui reste inline dans le layout ; une SURCHARGE par langue peut la
 * remplacer, stockée dans ContentString sous la clé `<blockId>.<field>`
 * (value = `Attachment.id`). `hydrate` applique ces clés plates par-dessus la
 * base sans traitement dédié.
 */
export const TRANSLATABLE_MEDIA_FIELDS: Record<string, string[]> = {
  Media: ["attachmentId"],
  TextImage: ["imageAttachmentId"],
  VideoHero: ["videoAttachmentId", "posterAttachmentId"],
};

/** Valeurs de BASE des champs média d'un layout : `blockId → { field: id }`. */
export function collectMediaBaseValues(
  layout: Data,
): Map<string, Record<string, string>> {
  const base = new Map<string, Record<string, string>>();
  walkPuckNodes(layout.content as PuckNode[], (node) => {
    const fields = node.type ? TRANSLATABLE_MEDIA_FIELDS[node.type] : undefined;
    if (!fields) return;
    const props = (node.props ?? {}) as Record<string, unknown> & { id?: string };
    const blockId = props.id ?? node.type ?? "";
    const entry: Record<string, string> = {};
    for (const field of fields) {
      const v = props[field];
      entry[field] = typeof v === "string" ? v : "";
    }
    base.set(blockId, entry);
  });
  return base;
}

/**
 * Déshydratation pour la SAUVEGARDE, avec images par langue (base + surcharges).
 * On part de `dehydrate` (texte) puis, pour chaque champ média :
 *  - langue par défaut → la valeur courante EST la base : on la laisse dans le
 *    layout, aucune surcharge émise ;
 *  - autre langue → on RESTAURE la base dans le layout (jamais écrasée par la
 *    valeur d'une langue non-défaut) et on émet une surcharge `<blockId>.<field>`
 *    = valeur choisie, ou "" si égale à la base (le diff du save supprime alors
 *    la surcharge → héritage rétabli).
 *
 * `base` = layout NEUTRE chargé depuis l'API (ses champs média portent la base).
 */
export function dehydrateForSave(
  data: Data,
  opts: { lang: string; defaultLang: string; base: Data },
): { neutralLayout: Data; strings: Record<string, string> } {
  const { neutralLayout, strings } = dehydrate(data);

  // Langue par défaut : les champs média restent tels quels dans le layout
  // (= la base). Rien de plus à faire (dehydrate ne touche pas aux médias).
  if (opts.lang === opts.defaultLang) return { neutralLayout, strings };

  const baseValues = collectMediaBaseValues(opts.base);
  const nextContent = mapPuckNodes(
    neutralLayout.content as PuckNode[],
    (type, props) => {
      const fields = type ? TRANSLATABLE_MEDIA_FIELDS[type] : undefined;
      if (!fields) return props;
      const blockId = (props.id as string | undefined) ?? type;
      const baseEntry = baseValues.get(blockId) ?? {};
      const next: Record<string, unknown> = { ...props };
      for (const field of fields) {
        const curVal =
          typeof props[field] === "string" ? (props[field] as string) : "";
        const baseVal = baseEntry[field] ?? "";
        next[field] = baseVal; // base restaurée dans le layout partagé
        strings[`${blockId}.${field}`] = curVal === baseVal ? "" : curVal;
      }
      return next;
    },
  );

  return {
    neutralLayout: { ...neutralLayout, content: nextContent } as Data,
    strings,
  };
}
